import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { SpinnerGap, Robot, ChartBar, ArrowsClockwise, FloppyDisk, CheckSquare, Square } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ModelRow, ModelAnalyticsPanel } from "./AdminHelpers";

export function AdminModelsSection() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [enabledModels, setEnabledModels] = useState([]);
  const [allEnabledDefault, setAllEnabledDefault] = useState(true);
  const [modelsSaving, setModelsSaving] = useState(false);
  const [modelsRefreshing, setModelsRefreshing] = useState(false);
  const [modelsView, setModelsView] = useState("controls");
  const [modelAnalytics, setModelAnalytics] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState("30d");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [budgetEditing, setBudgetEditing] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState({ monthly_budget: 0, alert_threshold_pct: 80 });
  const [budgetSaving, setBudgetSaving] = useState(false);

  const loadModels = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get("/admin/models");
      setModels(resp.data.models || []);
      setAllEnabledDefault(resp.data.all_enabled_default);
      setEnabledModels((resp.data.models || []).filter(m => m.enabled).map(m => m.name));
    } catch (e) { toast.error("Failed to load models"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadModels(); }, [loadModels]);

  const handleSaveModels = async () => {
    setModelsSaving(true);
    try { await api.patch("/admin/models", { enabled_models: enabledModels }); toast.success(`${enabledModels.length} models enabled`); setAllEnabledDefault(false); }
    catch (e) { toast.error("Failed to save models"); }
    setModelsSaving(false);
  };

  const handleRefreshCatalog = async () => {
    setModelsRefreshing(true);
    try { const resp = await api.post("/admin/models/refresh"); toast.success(`Fetched ${resp.data.models_fetched} models from OpenRouter`); await loadModels(); }
    catch (e) { toast.error("Failed to refresh catalog"); }
    setModelsRefreshing(false);
  };

  const toggleModel = (name) => setEnabledModels(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  const toggleAllModels = () => enabledModels.length === models.length ? setEnabledModels([]) : setEnabledModels(models.map(m => m.name));

  const loadModelAnalytics = async (period = "30d") => {
    setAnalyticsLoading(true);
    try {
      const resp = await api.get(`/admin/model-analytics?period=${period}`);
      setModelAnalytics(resp.data);
      if (resp.data.budget) setBudgetDraft({ monthly_budget: resp.data.budget.monthly_budget, alert_threshold_pct: resp.data.budget.alert_threshold_pct });
    } catch (e) { toast.error("Failed to load model analytics"); }
    finally { setAnalyticsLoading(false); }
  };

  const saveBudget = async () => {
    setBudgetSaving(true);
    try { const resp = await api.patch("/admin/cost-budget", budgetDraft); toast.success("Budget updated"); setBudgetEditing(false); if (modelAnalytics) setModelAnalytics({ ...modelAnalytics, budget: resp.data }); }
    catch (e) { toast.error("Failed to save budget"); }
    setBudgetSaving(false);
  };

  if (loading) return <div className="flex justify-center py-8"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-models-section">
      <div className="flex items-center justify-between">
        <div className="flex rounded-lg border border-[var(--border)] overflow-hidden" data-testid="models-view-toggle">
          <button onClick={() => setModelsView("controls")} className={`px-4 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${modelsView === "controls" ? "bg-[#FF4400] text-white" : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"}`} data-testid="models-view-controls"><Robot size={13} />Controls</button>
          <button onClick={() => { setModelsView("analytics"); if (!modelAnalytics) loadModelAnalytics(analyticsPeriod); }} className={`px-4 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${modelsView === "analytics" ? "bg-[#FF4400] text-white" : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"}`} data-testid="models-view-analytics"><ChartBar size={13} />Analytics</button>
        </div>
        {modelsView === "controls" && (
          <div className="flex items-center gap-2">
            <button onClick={handleRefreshCatalog} disabled={modelsRefreshing} className="px-3 py-1.5 rounded-md text-xs border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors flex items-center gap-1.5" data-testid="refresh-models-btn">{modelsRefreshing ? <SpinnerGap size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}Refresh from OpenRouter</button>
            <button onClick={handleSaveModels} disabled={modelsSaving} className="px-3 py-1.5 rounded-md text-xs bg-[#FF4400] text-white hover:bg-[#FF6633] transition-colors flex items-center gap-1.5" data-testid="save-models-btn">{modelsSaving ? <SpinnerGap size={12} className="animate-spin" /> : <FloppyDisk size={12} />}Save</button>
          </div>
        )}
      </div>

      {modelsView === "controls" && (
        <>
          {allEnabledDefault && <div className="px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs text-amber-400">All models are currently enabled by default. Check the ones you want and save to lock it down.</div>}
          <ModelList models={models} enabledModels={enabledModels} toggleAllModels={toggleAllModels} toggleModel={toggleModel} />
          <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">Only enabled models appear in the model selector for users. You (admin) always see all models regardless.</p>
        </>
      )}

      {modelsView === "analytics" && <ModelAnalyticsPanel analytics={modelAnalytics} loading={analyticsLoading} period={analyticsPeriod} onPeriodChange={(p) => { setAnalyticsPeriod(p); loadModelAnalytics(p); }} budgetEditing={budgetEditing} budgetDraft={budgetDraft} setBudgetDraft={setBudgetDraft} setBudgetEditing={setBudgetEditing} saveBudget={saveBudget} budgetSaving={budgetSaving} />}
    </div>
  );
}

function ModelList({ models, enabledModels, toggleAllModels, toggleModel }) {
  const coreModels = useMemo(() => models.filter(m => m.source === "configured"), [models]);
  const catalogModels = useMemo(() => models.filter(m => m.source === "catalog"), [models]);

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[var(--border)] bg-[var(--bg-base)] flex items-center justify-between">
        <button onClick={toggleAllModels} className="flex items-center gap-2 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid="toggle-all-models">
          {enabledModels.length === models.length ? <CheckSquare size={16} weight="fill" className="text-[#FF4400]" /> : <Square size={16} className="text-[var(--text-muted)]" />}
          {enabledModels.length === models.length ? "Deselect All" : "Select All"}
        </button>
        <span className="text-[10px] text-[var(--text-muted)] font-['JetBrains_Mono']">{enabledModels.length} / {models.length} enabled</span>
      </div>
      {coreModels.length > 0 && <div className="px-4 py-2 border-b border-[var(--border)]"><span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Core Models</span></div>}
      {coreModels.map(m => <ModelRow key={m.name} model={m} enabled={enabledModels.includes(m.name)} onToggle={() => toggleModel(m.name)} />)}
      {catalogModels.length > 0 && (
        <>
          <div className="px-4 py-2 border-b border-t border-[var(--border)]"><span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">OpenRouter Catalog</span></div>
          {catalogModels.map(m => <ModelRow key={m.name} model={m} enabled={enabledModels.includes(m.name)} onToggle={() => toggleModel(m.name)} />)}
        </>
      )}
    </div>
  );
}
